<script setup>
import { computed } from 'vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'primary',
    validator: (value) =>
      ['primary', 'secondary', 'outline', 'dark-outline', 'ghost', 'maroon', 'gold', 'white'].includes(value)
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  disabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  rounded: {
    type: Boolean,
    default: true
  },
  block: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'button',
    validator: (value) => ['button', 'submit', 'reset'].includes(value)
  },
  dropdown: {
    type: Boolean,
    default: false
  },
  dropdownOpen: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['click']);

const buttonClasses = computed(() => [
  'ui-button',
  `ui-button--${props.variant}`,
  `ui-button--${props.size}`,
  {
    'ui-button--rounded': props.rounded,
    'ui-button--block': props.block,
    'ui-button--disabled': props.disabled || props.loading,
    'ui-button--loading': props.loading
  }
]);

const isDisabled = computed(() => props.disabled || props.loading);

const handleClick = (event) => {
  if (isDisabled.value) return;
  emit('click', event);
};
</script>

<template>
  <button :class="buttonClasses" :disabled="isDisabled" :type="type" @click="handleClick" :aria-busy="loading">
    <!-- Start icon slot -->
    <span v-if="$slots.startIcon" class="ui-button__start-icon">
      <slot name="startIcon" />
    </span>

    <!-- Loading spinner -->
    <span v-if="loading" class="ui-button__spinner">
      <svg class="ui-button__spinner-svg" viewBox="0 0 24 24" fill="none">
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-dasharray="31.416"
          stroke-dashoffset="31.416"
          class="ui-button__spinner-circle"
        />
      </svg>
    </span>

    <!-- Button text -->
    <span :class="{ 'ui-button__text--hidden': loading }" class="ui-button__text">
      <slot />
    </span>

    <!-- End icon slot -->
    <span v-if="$slots.endIcon" class="ui-button__end-icon">
      <slot name="endIcon" />
    </span>

    <!-- Dropdown chevron -->
    <span v-if="dropdown" class="ui-button__chevron" :class="{ 'ui-button__chevron--open': dropdownOpen }">
      <svg width="16" height="16" viewBox="0 0 12 12" fill="currentColor">
        <path
          d="M3 4.5L6 7.5L9 4.5"
          stroke="currentColor"
          stroke-width="1.5"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
  </button>
</template>

<style scoped>
.ui-button {
  /* Base styles */
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  white-space: nowrap;
  user-select: none;
  vertical-align: middle;
  outline: none;

  /* Focus styles */
  &:focus-visible {
    outline: 2px solid #ffc627;
    outline-offset: 2px;
  }
}

/* Size variants */
.ui-button--small {
  height: 32px;
  padding: 6px 16px;
  font-size: 14px;
  line-height: 20px;
}

.ui-button--medium {
  height: 32px;
  padding: 4px 24px;
  font-size: 16px;
  line-height: 20px;
}

.ui-button--large {
  height: 40px;
  padding: 4px 24px;
  font-size: 16px;
  line-height: 20px;
}

/* Rounded styles */
.ui-button--rounded {
  border-radius: 50px;
}

.ui-button:not(.ui-button--rounded) {
  border-radius: 8px;
}

/* Block button */
.ui-button--block {
  display: flex;
  width: 100%;
}

/* Variant styles */
.ui-button--primary {
  background-color: #8c1d40;
  color: white;
}

.ui-button--primary:hover:not(.ui-button--disabled) {
  background-color: #731729;
}

.ui-button--secondary {
  background-color: #f5f5f5;
  color: #333;
}

.ui-button--secondary:hover:not(.ui-button--disabled) {
  background-color: #e8e8e8;
}

.ui-button--outline {
  background-color: transparent;
  color: #8c1d40;
  border: 2px solid #8c1d40;
}

.ui-button--outline:hover:not(.ui-button--disabled) {
  background-color: #8c1d40;
  color: white;
}

.ui-button--dark-outline {
  background-color: transparent;
  color: #333;
  border: 1px solid #333;
}

.ui-button--dark-outline:hover:not(.ui-button--disabled) {
  background-color: rgba(51, 51, 51, 0.1);
  color: #333;
}

.ui-button--ghost {
  background-color: transparent;
  color: #8c1d40;
}

.ui-button--ghost:hover:not(.ui-button--disabled) {
  background-color: rgba(140, 29, 64, 0.1);
}

.ui-button--maroon {
  background-color: #8c1d40;
  color: white;
}

.ui-button--maroon:hover:not(.ui-button--disabled) {
  background-color: #731729;
}

.ui-button--gold {
  background-color: #ffc627;
  color: #000;
}

.ui-button--gold:hover:not(.ui-button--disabled) {
  background-color: #e6b223;
}

.ui-button--white {
  background-color: white;
  color: #333;
  border: 1px solid #e0e0e0;
}

.ui-button--white:hover:not(.ui-button--disabled) {
  background-color: #f5f5f5;
}

/* Disabled state */
.ui-button--disabled {
  cursor: not-allowed;
  opacity: 0.5;
  background-color: #bfbfbf !important;
}

.ui-button--disabled:hover {
  transform: none;
}

/* Loading state */
.ui-button--loading {
  cursor: wait;
}

.ui-button__text--hidden {
  opacity: 0;
}

/* Icon styles */
.ui-button__start-icon,
.ui-button__end-icon,
.ui-button__chevron {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.ui-button__start-icon {
  margin-left: -4px;
}

.ui-button__end-icon {
  margin-right: -4px;
}

/* Chevron styles */
.ui-button__chevron {
  margin-left: 8px;
  margin-right: -2px;
  transition: transform 0.2s ease-in-out;
}

.ui-button__chevron--open {
  transform: rotate(180deg);
}

/* Spinner styles */
.ui-button__spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ui-button__spinner-svg {
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
}

.ui-button__spinner-circle {
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}
</style>
